/**
 * Implementation Checklist Component - Fiche de suivi pour l'emploi des mesures
 * Strategic Dashboard View
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle
} from 'lucide-react';

import useAtelier1Store from '../../../stores/useAtelier1Store';

// Import new components
import GlobalUnifiedChecklist from './GlobalUnifiedChecklist';
import ScenarioSidebar from './ScenarioSidebar';
import useSuiviMeasures from './useSuiviMeasures';

const ImplementationChecklist = ({
  analysisId,
  loading: externalLoading = false
}) => {
  const { t } = useTranslation();

  // Measures come from the Atelier 5 measures table (same builder rows),
  // not from the workshop5-checklist endpoint.
  const { allMeasures, error, isReady } = useSuiviMeasures(analysisId);

  // The responsible-person directory is owned by Atelier 1.  Keep this store
  // load local to the checklist so it remains correct when Atelier 5 is opened
  // without the rest of the workshop navigation being mounted.
  const responsiblePersonsList = useAtelier1Store((s) => s.contextData?.responsiblePersonsList || []);
  const isAtelier1DataLoaded = useAtelier1Store((s) => s.isDataLoaded);
  const atelier1AnalysisId = useAtelier1Store((s) => s.analysisId);
  const loadAtelier1Data = useAtelier1Store((s) => s.loadData);

  useEffect(() => {
    if (!analysisId) return;
    if (!isAtelier1DataLoaded || atelier1AnalysisId !== analysisId) {
      loadAtelier1Data(analysisId);
    }
  }, [analysisId, isAtelier1DataLoaded, atelier1AnalysisId, loadAtelier1Data]);

  const isAtelier1Ready = !analysisId || (
    isAtelier1DataLoaded && atelier1AnalysisId === analysisId
  );
  const loading = !isReady || !isAtelier1Ready || externalLoading;

  // Build the sidebar from the Atelier 1 directory, then attach the retained
  // Atelier 5 measures that belong to each person.
  const responsiblePersons = useMemo(() => {
    const notAssignedKey = t('implementationChecklist.notAssigned');
    const retainedMeasures = (allMeasures || []).filter((m) => m.retained !== false);
    const isUnassigned = (m) => {
      const responsible = m.responsiblePerson || m.responsibleParty;
      return !responsible ||
        responsible === 'Non affecté' ||
        responsible === 'Not assigned' ||
        responsible === 'Unassigned';
    };
    const getImplementedCount = (measures) => measures.filter(
      (m) => m.implementation === 'implemented' || m.decision === 'implemented'
    ).length;
    const buildMetrics = (person, measures) => {
      const implementedMeasures = getImplementedCount(measures);
      return {
        id: person.id,
        name: person.name,
        role: person.role,
        totalMeasures: measures.length,
        implementedMeasures,
        allMeasures: measures,
        advancementMetrics: {
          advancementScore: measures.length > 0
            ? Math.round((implementedMeasures / measures.length) * 100)
            : 0,
          totalMeasures: measures.length,
          implementedMeasures,
          riskReduction: 0,
        },
      };
    };

    const persons = responsiblePersonsList.map((person) => {
      const personMeasures = retainedMeasures.filter(
        (m) => (m.responsiblePerson || m.responsibleParty) === person.name
      );
      return buildMetrics(person, personMeasures);
    });

    const unassignedMeasures = retainedMeasures.filter(isUnassigned);
    if (unassignedMeasures.length > 0) {
      persons.push(buildMetrics(
        { id: notAssignedKey, name: notAssignedKey, role: '' },
        unassignedMeasures
      ));
    }

    return persons;
  }, [allMeasures, responsiblePersonsList, t]);

  const [selectedResponsible, setSelectedResponsible] = useState(null);

  // Error state takes precedence over the loading state.
  if (error) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">{t('common.error')}</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">{t('workshop5.checklist.loadingWorkshop5')}</span>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Main Content Area - Split View Layout */}
      <div className="flex-1 flex overflow-hidden">

        {/* LEFT SIDEBAR: Strategic Navigation */}
        <div className="w-64 flex-shrink-0 bg-white shadow-xl z-20 h-full overflow-hidden border-r border-gray-200">
          <ScenarioSidebar
            scenarios={responsiblePersons}
            selectedScenario={selectedResponsible}
            onSelectScenario={setSelectedResponsible}
            isResponsibleMode={true}
          />
        </div>

        {/* RIGHT CONTENT: Operational Workspace */}
        <div className="flex-1 overflow-y-auto bg-gray-50 relative custom-scrollbar">

          <div className="relative z-10 p-4 max-w-[1600px] mx-auto min-h-full pb-10">
            <GlobalUnifiedChecklist
              analysisId={analysisId}
              selectedResponsible={selectedResponsible}
              measures={allMeasures}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default ImplementationChecklist;
